import React, { useEffect, useState } from "react";
import { Button, ButtonGroup, Dropdown, DropdownButton, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useRouteLoaderData } from "react-router-dom";

import { useAppApi } from "@/App";
import { useRequest } from "@components/AppHook";
import {
    GamePublicationStateDisplay,
    GamePublicationWishlistStateDisplay,
} from "@components/Displays/GamePublicationStateDisplay";
import { DevForm } from "@components/Forms/Fields";
import { GameToBrowserButton, GameToGameCenterButton } from "@components/Game/Misc";
import { Awesome, DevModal, OrderingHeader, YesNoColumn } from "@components/Helpers";
import { DefaultLayout } from "@components/Layouts";
import { ModalFilterGames } from "@components/Modals";
import { ListingCard } from "@components/Pages";
import { humanTimer, url_reverse } from "@components/Utils";

const ls_key_super = "game_list_settings_super";
const ls_key_user = "game_list_settings";

export default function GameListScreen() {
    const commonInfo = useRouteLoaderData("commonInfo");
    const apiProps = useAppApi(url_reverse("api:game:list"));

    if (commonInfo.account) {
        // потому что сюда может прийти юзер без аккаунта
        return (
            <DefaultLayout>
                {commonInfo.account.is_super ? (
                    <ListSuper {...apiProps} />
                ) : commonInfo.account.pr_moderator ? (
                    <ListPrModerator {...apiProps} />
                ) : (
                    <ListUser {...apiProps} />
                )}
            </DefaultLayout>
        );
    }
}

function ListSuper({ apiData }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [colSettings, setColSettings] = useState(JSON.parse(localStorage.getItem(ls_key_super) || "{}"));
    const [showColSettings, setShowColSettings] = useState(false);
    const [recreateCatalogErrors, setRecreateCatalogErrors] = useState(false);
    const [spinRecreate, setSpinRecreate] = useState(false);
    const commonInfo = useRouteLoaderData("commonInfo");
    const { api_post } = useRequest();

    useEffect(() => {
        if (apiData?.object_list?.length === 1 && apiData.is_quick_search) {
            navigate(url_reverse("games:page", { game_id: apiData.object_list[0].id }));
        }
    }, [apiData]);

    if (!apiData) return;

    const recreateCatalog = (ev, gcTypeId) => {
        setSpinRecreate(true);
        api_post(ev, url_reverse("api:gamecenter:recreate_catalog"), {
            params: { gc_type_id: gcTypeId },
            then: (resp) => {
                if (resp.status === "ok") {
                    alert("Каталог пересобран");
                } else {
                    setRecreateCatalogErrors(resp);
                }
                setSpinRecreate(false);
            },
        });
    };

    const gcSync = (ev) => {
        api_post(ev, url_reverse("api:gamecenter:gc_resync"), {
            then: (resp) => {
                if (resp.status === "ok") {
                    alert("Таск поставлен");
                }
            },
        });
    };

    const header = (
        <>
            {t("Игры")} <small>({apiData.pager?.count})</small>
        </>
    );
    const headerTools = (
        <>
            <DropdownButton as={ButtonGroup} drop="down" size="sm" variant="dark" title={t("Действия")}>
                <Dropdown.Item name="recreate_pub" onClick={(ev) => recreateCatalog(ev, 0)}>
                    <Awesome type="refresh" spin={spinRecreate} /> {t("Пересобрать каталог")}
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item name="gc_resync" onClick={gcSync}>
                    {t("Синк игр в ИЦ")}
                </Dropdown.Item>
            </DropdownButton>{" "}
            <Button href="#" variant="dark" size="sm" onClick={() => setShowColSettings(true)}>
                <Awesome type="cog" />
            </Button>{" "}
            {commonInfo.account.own_developers && (
                <Link to={url_reverse("games:create:devchoose")} className="btn btn-sm btn-dark">
                    <Awesome type="plus-circle" /> {t("Добавить")}
                </Link>
            )}
        </>
    );

    return (
        <ListingCard header={header} headerTools={headerTools} filterComponent={ModalFilterGames}>
            <ModalSettingsSuper
                show={showColSettings}
                setShow={setShowColSettings}
                colSettings={colSettings}
                setColSettings={setColSettings}
            />
            <ModalRecreateCatalogErrors resp={recreateCatalogErrors} setShow={setRecreateCatalogErrors} />
            {apiData.object_list?.length > 0 ? (
                <Table hover responsive className="dmg-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            {colSettings.gc_id && <th>{t("ID в ИЦ")}</th>}
                            {colSettings.my_tracker_app_id && <th>{t("ID в My.Tracker")}</th>}
                            {colSettings.technical_name && <th>{t("Идентификатор наименования")}</th>}
                            {colSettings.slug && <th>slug</th>}
                            <th>{t("Название")}</th>
                            <th>{t("Компания")}</th>
                            {colSettings.publication_state && <th>{t("Модерация проекта")}</th>}
                            {colSettings.publication_wishlist_state && <th>{t("Модерация страницы")}</th>}
                            {colSettings.date_state && (
                                <th>
                                    <OrderingHeader title={t("Время ожидания")} attr="date_state" />
                                </th>
                            )}
                            <th>{t("Публикация в каталоге VK Play")}</th>
                            <th>{t("Подтип")}</th>
                            {colSettings.gc_server_type && <th>{t("Сервер ИЦ")}</th>}
                            {colSettings.game_labels && <th>{t("Метки")}</th>}
                            <th style={{ width: "80px" }} />
                        </tr>
                    </thead>
                    <tbody>
                        {apiData.object_list.map((o) => (
                            <tr key={`object_list${o.id}`}>
                                <td>{o.id}</td>
                                {colSettings.gc_id && <td>{o.gc_id}</td>}
                                {colSettings.my_tracker_app_id && <td>{o.my_tracker_app_id}</td>}
                                {colSettings.technical_name && <td>{o.technical_name}</td>}
                                {colSettings.slug && <td>{o.slug}</td>}
                                <td>
                                    <Link to={url_reverse("games:page", { game_id: o.id })}>{o.name || "---"}</Link>
                                </td>
                                <td>
                                    <Link to={url_reverse("devs:edit", { dev_id: o.developer.id })}>
                                        {o.developer.name}
                                    </Link>
                                </td>
                                {colSettings.publication_state && (
                                    <td>
                                        <GamePublicationStateDisplay state={o.publication_state} />
                                    </td>
                                )}
                                {colSettings.publication_wishlist_state && (
                                    <td>
                                        <GamePublicationWishlistStateDisplay state={o.publication_wishlist_state} />
                                    </td>
                                )}
                                {colSettings.date_state && <td>{humanTimer(o.date_state, commonInfo.now)}</td>}
                                <td>
                                    <YesNoColumn attr={o.catalog_is_published} />
                                </td>
                                <td>{o.type_label}</td>
                                {colSettings.gc_server_type && <td>{o.gc_server_type_label}</td>}
                                {colSettings.game_labels && <td>{o.game_labels?.join(", ")}</td>}
                                <td className="dmg-table-row-tools">
                                    {o.in_archive ? (
                                        <Awesome type="eye-slash" color="red" />
                                    ) : (
                                        <Awesome type="eye" color="green" />
                                    )}
                                    <GameToBrowserButton game={o} />
                                    <GameToGameCenterButton game={o} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            ) : (
                t("У вас пока нет игр")
            )}
        </ListingCard>
    );
}

function ListPrModerator({ apiData }) {
    const { t } = useTranslation();

    if (!apiData) return;

    return (
        <ListingCard header={t("Игры")}>
            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>{t("Название")}</th>
                        <th>{t("Разработчик")}</th>
                        <th style={{ width: "80px" }} />
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list.map((o) => (
                        <tr key={`object_list${o.id}`}>
                            <td>{o.id}</td>
                            <td>{o.name || "---"}</td>
                            <td>{o.developer.name}</td>
                            <td className="dmg-table-row-tools">
                                <GameToBrowserButton game={o} />
                                <GameToGameCenterButton game={o} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </ListingCard>
    );
}

function ListUser({ apiData, setApiData, apiUrl }) {
    const { t } = useTranslation();
    const [colSettings, setColSettings] = useState(JSON.parse(localStorage.getItem(ls_key_user) || "{}"));
    const [showColSettings, setShowColSettings] = useState(false);
    const commonInfo = useRouteLoaderData("commonInfo");
    const { api_post } = useRequest();

    if (!apiData) return;

    const header = t("Ваши игры");
    const headerTools = (
        <>
            {!apiData.show_archive && (
                <>
                    <Link to={url_reverse("games:list", null, { archive: 1 })} className="btn btn-sm btn-dark">
                        <Awesome type="archive" /> {t("Архив")}
                    </Link>{" "}
                </>
            )}
            <Button href="#" variant="dark" size="sm" onClick={() => setShowColSettings(true)}>
                <Awesome type="cog" />
            </Button>{" "}
            {commonInfo.account.own_developers && (
                <Link to={url_reverse("games:create:devchoose")} className="btn btn-sm btn-dark">
                    <Awesome type="plus-circle" /> {t("Добавить")}
                </Link>
            )}
        </>
    );

    const handleArchive = (game_id) => {
        api_post(null, apiUrl, {
            params: { action: "handle_archive", game_id },
            then: (resp) => {
                if (resp.status === "ok") {
                    const newApiData = { ...apiData };
                    newApiData.object_list = newApiData.object_list.filter((o) => o.id !== game_id);
                    setApiData(newApiData);
                }
            },
        });
    };

    return (
        <ListingCard header={header} headerTools={headerTools}>
            <ModalSettingsUser
                show={showColSettings}
                setShow={setShowColSettings}
                colSettings={colSettings}
                setColSettings={setColSettings}
            />
            {apiData.object_list?.length > 0 ? (
                <Table hover responsive className="dmg-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            {colSettings.gc_id && <th>{t("ID в ИЦ")}</th>}
                            <th>{t("Название")}</th>
                            <th>{t("Модерация проекта")}</th>
                            <th>{t("Модерация страницы")}</th>
                            <th>{t("Публикация в каталоге VK Play")}</th>
                            <th style={{ width: "80px" }} />
                        </tr>
                    </thead>
                    <tbody>
                        {apiData.object_list.map((o) => (
                            <tr key={`object_list${o.id}`}>
                                <td>{o.id}</td>
                                {colSettings.gc_id && <td>{o.gc_id}</td>}
                                <td>
                                    <Link to={url_reverse("games:page", { game_id: o.id })}>{o.name || "---"}</Link>
                                </td>
                                <td>
                                    <GamePublicationStateDisplay state={o.publication_state} />
                                </td>
                                <td>
                                    <GamePublicationWishlistStateDisplay state={o.publication_wishlist_state} />
                                </td>
                                <td>
                                    <YesNoColumn attr={o.catalog_is_published} />
                                </td>
                                <td className="dmg-table-row-tools">
                                    <a href="#" onClick={() => handleArchive(o.id)}>
                                        {o.in_archive ? (
                                            <Awesome type="eye-slash" tooltip={t("Убрать из архива")} />
                                        ) : (
                                            <Awesome type="eye" tooltip={t("Переместить в архив")} />
                                        )}
                                    </a>
                                    <GameToBrowserButton game={o} />
                                    <GameToGameCenterButton game={o} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            ) : (
                t("У вас пока нет игр")
            )}
        </ListingCard>
    );
}

function ModalSettingsSuper({ show, setShow, colSettings, setColSettings }) {
    const { t } = useTranslation();

    const handleClose = () => setShow(false);

    const changeSettings = (ev) => {
        const newColSettings = { ...colSettings };
        newColSettings[ev.target.name] = ev.target.checked;
        setColSettings(newColSettings);
        localStorage.setItem(ls_key_super, JSON.stringify(newColSettings));
    };

    return (
        <DevModal
            size="lg"
            show={show}
            onHide={handleClose}
            header={t("Настройка колонок")}
            body={
                <>
                    <DevForm.Checkbox
                        name="gc_id"
                        label={t("ID в ИЦ")}
                        checked={colSettings.gc_id}
                        onChange={changeSettings}
                    />
                    <DevForm.Checkbox
                        name="my_tracker_app_id"
                        label={t("ID в My.Tracker")}
                        checked={colSettings.my_tracker_app_id || false}
                        onChange={changeSettings}
                    />
                    <DevForm.Checkbox
                        name="slug"
                        label="slug"
                        checked={colSettings.slug || false}
                        onChange={changeSettings}
                    />
                    <DevForm.Checkbox
                        name="date_state"
                        label={t("Время ожидания")}
                        checked={colSettings.date_state || false}
                        onChange={changeSettings}
                    />
                    <DevForm.Checkbox
                        name="gc_server_type"
                        label={t("Сервер ИЦ")}
                        checked={colSettings.gc_server_type || false}
                        onChange={changeSettings}
                    />
                    <DevForm.Checkbox
                        name="technical_name"
                        label={t("Идентификатор наименования")}
                        checked={colSettings.technical_name || false}
                        onChange={changeSettings}
                    />
                    <DevForm.Checkbox
                        name="publication_state"
                        label={t("Модерация проекта")}
                        checked={colSettings.publication_state || false}
                        onChange={changeSettings}
                    />
                    <DevForm.Checkbox
                        name="publication_wishlist_state"
                        label={t("Модерация страницы")}
                        checked={colSettings.publication_wishlist_state || false}
                        onChange={changeSettings}
                    />
                    <DevForm.Checkbox
                        name="game_labels"
                        label={t("Метки")}
                        checked={colSettings.game_labels || false}
                        onChange={changeSettings}
                    />
                </>
            }
        />
    );
}

function ModalSettingsUser({ show, setShow, colSettings, setColSettings }) {
    const { t } = useTranslation();

    const handleClose = () => setShow(false);

    const changeSettings = (ev) => {
        const newColSettings = { ...colSettings };
        newColSettings[ev.target.name] = ev.target.checked;
        setColSettings(newColSettings);
        localStorage.setItem(ls_key_user, JSON.stringify(newColSettings));
    };

    return (
        <DevModal
            size="lg"
            show={show}
            onHide={handleClose}
            header={t("Настройка колонок")}
            body={
                <DevForm.Checkbox
                    name="gc_id"
                    label={t("ID в ИЦ")}
                    checked={colSettings.gc_id}
                    onChange={changeSettings}
                />
            }
        />
    );
}

function ModalRecreateCatalogErrors({ resp, setShow }) {
    const { t } = useTranslation();

    const handleClose = () => setShow(false);

    return (
        <DevModal
            size="lg"
            show={resp}
            onHide={handleClose}
            header={t("Не удалось пересобрать каталог")}
            body={
                resp.urls?.length > 0 &&
                resp.urls.map((o, ind) => (
                    <div key={`rcu_${ind}`}>
                        <a target="_blank" href={o}>
                            {o}
                        </a>
                    </div>
                ))
            }
        />
    );
}
